import {
  ContentType,
  KeySystem,
  type LoadRequest,
  ManifestConfiguration,
  ManifestSourceConfiguration,
  ManifestType,
  type UiPluginConfig,
} from "@foundation-player/api";
import type { Config, Media, Rendition } from "./media_player.types.js";
import type { Plugin } from "./media_plugin.js";

function mergeObjects<TObject extends object, TSource extends object>(
  target: TObject,
  source: TSource,
): TObject & TSource {
  if (!isObject(target) || !isObject(source)) {
    return source as TObject & TSource;
  }

  Object.keys(source).forEach((key) => {
    const targetValue = (target as any)[key];
    const sourceValue = (source as any)[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      // Merge arrays by concatenation
      (target as any)[key] = targetValue.concat(sourceValue);
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      // Recursively merge nested objects
      (target as any)[key] = mergeObjects(targetValue, sourceValue);
    } else {
      // Direct assignment for other types
      (target as any)[key] = sourceValue;
    }
  });

  return target as TObject & TSource;
}

function isObject(value: any): value is object {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * Base class of configurations for Publishing Player. Must be extended by
 * classes for video and audio source.
 */
export abstract class PlayerConfig implements Config {
  protected doAutoplay = false;
  protected media: Media;

  readonly #licenseServerAddress: Record<
    "fairplay" | "playready" | "widevine",
    string
  > = {
    fairplay: "https://fairplay.rtl.de/Fairplay/license",
    playready: "https://playready-core.rtl.de/playready/api/license.asmx",
    widevine: "https://widevine.rtl.de/index/license",
  };

  protected abstract uiMode: "audio" | "video";

  protected constructor(media: Media) {
    this.media = media;
  }

  async #getConsent(): Promise<string> {
    const sourcepoint = document.querySelector(
      "ws-sourcepoint",
    ) as HTMLElement | null;

    if (sourcepoint) {
      await window.customElements.whenDefined("ws-sourcepoint");
      // Adjust the type as needed
      return await (sourcepoint as any).getTcString();
    } else {
      return ""; // Fallback if sourcepoint is not present
    }
  }

  // https://docs.player.foundation/v2/api/type/load-request/
  public async getLoadRequest(plugins: Set<Plugin>): Promise<LoadRequest> {
    const loadRequest: LoadRequest = {
      contentType: this.media.isAudioOnly
        ? ContentType.Podcast
        : ContentType.Vod,
      contentId: this.media.id,
      user: {
        consentString: await this.#getConsent(),
      },
      remoteConfig: {
        environment: this.media.environment,
        clientIdentifier: `html5/rtldigitalnews/${this.media.scope}`,
      },
      autoplay: this.doAutoplay,
      contentMetadata: {
        headline: this.media.headline,
      },
      plugins: {},
      manifests: this.createManifestsFromData(),
    };

    if (this.media.kicker) {
      loadRequest.contentMetadata!.secondaryHeadline = this.media.kicker;
    }
    if (this.media.durationInSeconds) {
      loadRequest.contentMetadata!.durationInSeconds =
        this.media.durationInSeconds;
    }
    if (this.media.posterUrl) {
      loadRequest.contentMetadata!.posterUrl = this.media.posterUrl;
    }

    // Merge loadRequest properties from plugins
    await Promise.all(
      [...plugins].map(async (plugin: Plugin) => {
        if (plugin.addFoundationLoadRequestProperties && plugin.isEnabled()) {
          mergeObjects(
            loadRequest as LoadRequest,
            (await plugin.addFoundationLoadRequestProperties()) as LoadRequest,
          );
        }
      }),
    );

    // Merge loadRequest.plugins from plugins
    await Promise.all(
      [...plugins].map(async (plugin: Plugin) => {
        if (plugin.addFoundationLoadRequestPlugins && plugin.isEnabled()) {
          mergeObjects(
            loadRequest.plugins as LoadRequest,
            (await plugin.addFoundationLoadRequestPlugins()) as LoadRequest,
          );
        }
      }),
    );

    console.info("Foundation Player", "loadRequest:", loadRequest);

    return loadRequest;
  }

  // https://docs.player.foundation/v2/api/type/ui-plugin-config/
  public async getUiPluginConfig(): Promise<UiPluginConfig> {
    const prefersDarkMode = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const theme =
      this.uiMode === "audio" && !prefersDarkMode ? "light" : "dark";
    const fullscreenOnlyEmitsEvents = this.uiMode === "video"; // Required for PluginFullscreen

    const uiPluginConfig: UiPluginConfig = {
      enabled: true,
      features: {
        fullscreenOnlyEmitsEvents,
      },
      look: {
        theme,
      },
    };

    console.info("Foundation Player", "uiPluginConfig:", uiPluginConfig);

    return uiPluginConfig;
  }

  /**
   * Create manifests for Publishing Player from manifests in data-renditions attribute
   */
  protected createManifestsFromData(): ManifestConfiguration[] {
    const manifests: ManifestConfiguration[] = [];
    for (const type in this.media.renditions) {
      // https://docs.player.foundation/v2/api/variable/manifest-type/
      if (
        !Object.hasOwn(this.media.renditions, type) ||
        !["dash", "hls", "progressive"].includes(type)
      ) {
        continue;
      }

      const manifestSources: ManifestSourceConfiguration[] = [];
      manifestSources.push({
        // @ts-expect-error TS7053: Element implicitly has an any type because expression of type string can't be used
        // to index type Renditions
        url: (this.media.renditions[type] as Rendition).url,
      });
      const foundationManifest: ManifestConfiguration = {
        sources: manifestSources,
        type: type as ManifestType,
      };
      // License server do not respond on localhost because of CORS restrictions.
      // Testing media with drm can only be tested in review app or changing hosts file.
      if (this.media.hasDrm) {
        foundationManifest.licenseServers = this.#getLicenseServers();
      }
      manifests.push(foundationManifest);
    }

    return manifests;
  }

  // https://docs.player.foundation/v2/api/type/manifest-configuration/#licenseservers
  #getLicenseServers(): {
    url: string;
    type: KeySystem;
    certificate?: string;
    headers?: {
      [k: string]: string | undefined;
    };
  }[] {
    return [
      {
        url: this.#licenseServerAddress.fairplay,
        certificate: "https://fairplay.rtl.de/Certificate/RTLiDe_cert.cer",
        type: KeySystem.Fairplay,
      },
      {
        url: this.#licenseServerAddress.playready,
        type: KeySystem.Playready,
      },
      {
        url: this.#licenseServerAddress.widevine,
        type: KeySystem.Widevine,
      },
    ];
  }
}
